/**
 * Helpers for variance analysis GL activity log display text.
 * Commentary inline-save entries use labels such as:
 * Local Proforma commentary saved, Regional Forecast commentary saved, etc.
 */

const COMMENTARY_SAVE_LOG_TYPE_PATTERN = /^[af]_(local|regional).*(proforma|forecast).*(save|saved)|^[af]_(local|regional)(proforma|forecast)(commentary)?saved$/i;

export const COMMENTARY_SAVE_ACTIVITY_LOG_CONFIG = {
  a_localproformasaved: { icon: 'mdi-content-save', color: 'info' },
  a_localforecastsaved: { icon: 'mdi-content-save', color: 'info' },
  a_regionalproformasaved: { icon: 'mdi-content-save', color: 'info' },
  a_regionalforecastsaved: { icon: 'mdi-content-save', color: 'info' },
  f_localproformasaved: { icon: 'mdi-content-save', color: 'info' },
  f_localforecastsaved: { icon: 'mdi-content-save', color: 'info' },
  f_regionalproformasaved: { icon: 'mdi-content-save', color: 'info' },
  f_regionalforecastsaved: { icon: 'mdi-content-save', color: 'info' },
};

/**
 * @param {string} reportingType
 * @returns {boolean}
 */
export function isBalanceSheetReportingType(reportingType = '') {
  const normalized = String(reportingType || '').trim().toLowerCase();
  return normalized === 'bs'
    || normalized === 'balance_sheet'
    || normalized === 'balance sheet';
}

/**
 * Resolve reporting type from a log entry and/or view context.
 * @param {Object} item
 * @param {{ reportingType?: string, filter?: Object }} options
 */
export function resolveActivityLogReportingType(item = {}, options = {}) {
  return item.reporting_type
    || item.reportingType
    || options.filter?.reporting_type
    || options.filter?.reportingType
    || options.reportingType
    || '';
}

/**
 * @param {Function} labelsName - this.$nova.labelsName
 * @param {string} reportingType - 'bs' | 'pl' | ''
 */
export function getVarianceActivityLogLabels(labelsName, reportingType = '') {
  const labelFn = typeof labelsName === 'function' ? labelsName : () => '';
  const isBS = isBalanceSheetReportingType(reportingType);

  return {
    Local: labelFn('my_reporting_units_page', 'commentary_local_key_label', 'Local'),
    Regional: labelFn('my_reporting_units_page', 'commentary_regional_key_label', 'Regional'),
    Proforma: isBS
      ? labelFn('cycle_page', 'proforma_shortform_label_balancesheet', 'Proforma')
      : labelFn('cycle_page', 'proforma_shortform_label', 'Proforma'),
    Forecast: isBS
      ? labelFn('cycle_page', 'forecast_shortform_label_balancesheet', 'Forecast')
      : labelFn('cycle_page', 'forecast_shortform_label', 'Forecast'),
    RU: labelFn('my_reporting_units_page', 'commenrary_ru_label', 'RU'),
    LA: labelFn('my_reporting_units_page', 'local_approver_log', 'LA'),
    RA: labelFn('my_reporting_units_page', 'regional_approver_log', 'RA'),
  };
}

/**
 * @param {string} type
 * @returns {{ scope: 'local'|'regional', field: 'proforma'|'forecast' }|null}
 */
export function parseCommentarySaveLogType(type) {
  if (!type || typeof type !== 'string') {
    return null;
  }

  const normalized = type.toLowerCase();
  if (normalized.includes('reason')) {
    return null;
  }

  if (!COMMENTARY_SAVE_LOG_TYPE_PATTERN.test(normalized)) {
    return null;
  }

  const scope = normalized.includes('regional')
    ? 'regional'
    : normalized.includes('local')
      ? 'local'
      : null;
  const field = normalized.includes('proforma')
    ? 'proforma'
    : normalized.includes('forecast')
      ? 'forecast'
      : null;

  if (!scope || !field) {
    return null;
  }

  return { scope, field };
}

/**
 * @param {string} text
 * @param {ReturnType<typeof getVarianceActivityLogLabels>} labels
 * @param {{ reportingType?: string, identifyAndRemapLabelName?: Function }} [options]
 */
export function applyVarianceActivityLogLabelReplacements(text, labels, options = {}) {
  if (!text || typeof text !== 'string') {
    return text;
  }

  let formatted = text
    .replace(/Regional/gi, labels.Regional)
    .replace(/\bRU\b/g, labels.RU)
    .replace(/local/gi, labels.Local)
    .replace(/\bLA\b/g, labels.LA)
    .replace(/\bRA\b/g, labels.RA)
    .replace(/\bProforma\b/gi, labels.Proforma)
    .replace(/\bForecast\b/gi, labels.Forecast);

  if (
    isBalanceSheetReportingType(options.reportingType)
    && typeof options.identifyAndRemapLabelName === 'function'
  ) {
    formatted = options.identifyAndRemapLabelName('cycle_page', formatted, true);
  }

  return formatted;
}

/**
 * @param {{ scope: 'local'|'regional', field: 'proforma'|'forecast' }} parsed
 * @param {ReturnType<typeof getVarianceActivityLogLabels>} labels
 */
export function buildCommentarySaveLogText(parsed, labels) {
  const scopeLabel = parsed.scope === 'regional' ? labels.Regional : labels.Local;
  const fieldLabel = parsed.field === 'proforma' ? labels.Proforma : labels.Forecast;
  return `${scopeLabel} ${fieldLabel} commentary saved`;
}

/**
 * @param {Object} item
 * @param {{ reportingType?: string, filter?: Object, labelsName?: Function, identifyAndRemapLabelName?: Function }} options
 */
export function formatVarianceActivityLogEntry(item, options = {}) {
  if (!item || typeof item !== 'object') {
    return item;
  }

  const reportingType = resolveActivityLogReportingType(item, options);
  const labels = getVarianceActivityLogLabels(options.labelsName, reportingType);
  const replacementOptions = {
    reportingType,
    identifyAndRemapLabelName: options.identifyAndRemapLabelName,
  };
  const parsed = parseCommentarySaveLogType(item.type);
  let text = item.text;

  if (parsed) {
    text = buildCommentarySaveLogText(parsed, labels);
    text = applyVarianceActivityLogLabelReplacements(text, labels, replacementOptions);
  } else if (typeof text === 'string') {
    text = applyVarianceActivityLogLabelReplacements(text, labels, replacementOptions);
  }

  return {
    ...item,
    text,
  };
}

/**
 * @param {Array} logs
 * @param {{ reportingType?: string, filter?: Object, labelsName?: Function, identifyAndRemapLabelName?: Function }} options
 */
export function formatVarianceActivityLogEntries(logs, options = {}) {
  if (!Array.isArray(logs)) {
    return [];
  }

  return logs.map((item) => formatVarianceActivityLogEntry(item, options));
}
